"use client";

import {ClientToServerEvents, ServerToClientEvents} from "@/ws/types";
import logger from "@/app/logger";
import {WsAck, WsResponse} from "@/pb/api/devplan/services/agent/chats";
import {v4 as uuid} from 'uuid';

export interface WebSocketClientOptions {
  reconnection?: boolean;
  reconnectionAttempts?: number;
  reconnectionDelay?: number;
  reconnectionDelayMax?: number;
  timeout?: number;
  autoConnect?: boolean;
}

export interface WebSocketClient<ServerEvents, ClientEvents> {
  connected: boolean;

  emit(event: keyof ClientEvents, ...args: unknown[]): void;

  on<K extends keyof ServerEvents | 'connect' | 'disconnect'>(
    event: K,
    handler: K extends keyof ServerEvents ? ServerEvents[K] : () => void
  ): void;

  off<K extends keyof ServerEvents | 'connect' | 'disconnect'>(
    event: K,
    handler: K extends keyof ServerEvents ? ServerEvents[K] : () => void
  ): void;

  removeListener<K extends keyof ServerEvents | 'connect' | 'disconnect'>(
    event: K,
    handler: K extends keyof ServerEvents ? ServerEvents[K] : () => void
  ): void;
}

class WebSocketClientImpl<ServerEvents, ClientEvents> implements WebSocketClient<ServerEvents, ClientEvents> {
  private ws: WebSocket | null = null;
  private listeners: Map<string, ((...args: unknown[]) => void)[]> = new Map();
  private reconnectAttempts = 0;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private url: string;
  private options: Required<WebSocketClientOptions>;
  private _connected = false;

  constructor(url: string, options: WebSocketClientOptions = {}) {
    this.url = url;
    this.options = {
      reconnection: options.reconnection ?? true,
      reconnectionAttempts: options.reconnectionAttempts ?? 100,
      reconnectionDelay: options.reconnectionDelay ?? 5000,
      reconnectionDelayMax: options.reconnectionDelayMax ?? 20000,
      timeout: options.timeout ?? 20000,
      autoConnect: options.autoConnect ?? true,
    };

    if (this.options.autoConnect) {
      this.connect();
    }
  }

  get connected(): boolean {
    return this._connected;
  }

  private connect(): void {
    if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
      return;
    }

    try {
      this.ws = new WebSocket(this.url);

      // Set up connection timeout
      const timeout = setTimeout(() => {
        if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
          this.ws.close();
        }
      }, this.options.timeout);

      this.ws.onopen = () => {
        clearTimeout(timeout);
        this._connected = true;
        this.reconnectAttempts = 0;
        this.trigger("connect" as keyof ServerEvents, undefined, true);
      };

      this.ws.onmessage = (event: MessageEvent) => {
        decodeWsResponse(event.data)
          .then(r => this.processWsResponse(r))
          .catch(error => logger.error({error}, 'Failed to decode message'))
      };

      this.ws.onclose = () => {
        clearTimeout(timeout);
        const wasConnected = this._connected;
        this._connected = false;

        if (wasConnected) {
          this.trigger("disconnect" as keyof ServerEvents, undefined, true);
        }

        if (this.options.reconnection && this.reconnectAttempts < this.options.reconnectionAttempts) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        clearTimeout(timeout);
        console.error('WebSocket error:', error);
      };

    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      if (this.options.reconnection && this.reconnectAttempts < this.options.reconnectionAttempts) {
        this.scheduleReconnect();
      }
    }
  }

  private processWsResponse(resp: WsResponse): void {
    switch (resp.type?.$case) {
      case 'ack':
        this.triggerAck(resp.type.value);
        break
      case 'runProjectAgent':
        this.trigger('runProjectAgentResponse' as keyof ServerEvents, resp.type.value);
        break
      case 'chatSession':
        this.trigger('chatSessionResponse' as keyof ServerEvents, resp.type.value);
        break
      case 'threadActivityUpdate':
        this.trigger('threadExecutionUpdates' as keyof ServerEvents, resp.type.value);
        break
      case 'threadProgressUpdate':
        this.trigger('threadProgressUpdates' as keyof ServerEvents, resp.type.value);
        break
      default:
        logger.error({resp}, 'Unexpected response type');
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.options.reconnectionDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.options.reconnectionDelayMax
    );

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private trigger<K extends keyof ServerEvents>(event: K, resp: unknown, noData = false): void {
    const handlers = this.listeners.get(event as string);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          if (noData) {
            handler();
          } else {
            handler(resp);
          }
        } catch (error) {
          console.error(`Error in event handler for ${String(event)}:`, error);
        }
      });
    }
  }

  private triggerAck({ackId}: WsAck): void {
    const handlers = this.listeners.get(ackId);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler();
        } catch (error) {
          logger.error({error, ackId}, "Error in ack handler");
        }
      });
      this.listeners.delete(ackId);
    }
  }

  emit(event: keyof ClientEvents, ...args: unknown[]): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        // Handle acknowledgment callback pattern
        const data = args[0];
        const lastArg = args.length > 1 ? args[args.length - 1] : undefined;
        let ackCallback: (() => void) | undefined = undefined;

        if (lastArg && typeof lastArg === 'function') {
          ackCallback = lastArg as () => void;
        }

        const message = {
          event: event as string,
          data,
          ackId: ackCallback ? `${uuid()}` : undefined,
        };

        if (message.ackId && ackCallback) {
          this.listeners.set(message.ackId, [ackCallback]);
        }

        const encoded = JSON.stringify(message);
        this.ws.send(encoded);
      } catch (error) {
        console.error('Failed to send message:', error);
      }
    } else {
      console.warn(`Cannot emit ${String(event)}: WebSocket not connected`);
    }
  }

  on<K extends keyof ServerEvents | 'connect' | 'disconnect'>(
    event: K,
    handler: K extends keyof ServerEvents ? ServerEvents[K] : () => void
  ): void {
    const eventName = event as string;
    if (!this.listeners.has(eventName)) {
      this.listeners.set(eventName, []);
    }
    this.listeners.get(eventName)!.push(handler as (...args: unknown[]) => void);
  }

  off<K extends keyof ServerEvents | 'connect' | 'disconnect'>(
    event: K,
    handler: K extends keyof ServerEvents ? ServerEvents[K] : () => void
  ): void {
    this.removeListener(event, handler);
  }

  removeListener<K extends keyof ServerEvents | 'connect' | 'disconnect'>(
    event: K,
    handler: K extends keyof ServerEvents ? ServerEvents[K] : () => void
  ): void {
    const eventName = event as string;
    const handlers = this.listeners.get(eventName);
    if (handlers) {
      const index = handlers.indexOf(handler as (...args: unknown[]) => void);
      if (index !== -1) {
        handlers.splice(index, 1);
      }
      if (handlers.length === 0) {
        this.listeners.delete(eventName);
      }
    }
  }

  disconnect(): void {
    this.options.reconnection = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }
}

export function createWebSocketClient<ServerEvents, ClientEvents>(
  url: string,
  options: WebSocketClientOptions = {}
): WebSocketClient<ServerEvents, ClientEvents> {
  return new WebSocketClientImpl<ServerEvents, ClientEvents>(url, options);
}

// Create the main socket instance
let _socket: WebSocketClient<ServerToClientEvents, ClientToServerEvents> | undefined = undefined;

export function getSocket(): WebSocketClient<ServerToClientEvents, ClientToServerEvents> | undefined {
  if (_socket === undefined) {
    if (typeof window === 'undefined') {
      return undefined
    }
    _socket = createWebSocketClient(
      getWsUrl(),
      {
        reconnection: true,
        reconnectionAttempts: 100,
        reconnectionDelay: 5000,
        reconnectionDelayMax: 20000,
        timeout: 20000,
        autoConnect: true,
      }
    );
  }
  return _socket;
}

function getWsUrl() {
  if (typeof window === 'undefined') {
    // During SSR, this shouldn't be called, but provide fallback
    throw new Error('WebSocket URL cannot be determined during server-side rendering');
  }
  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const host = window.location.host;
  return `${protocol}://${host}/ws`;
}

async function decodeWsResponse(data: unknown): Promise<WsResponse> {
  let buf: Uint8Array;

  // Convert event.data to ArrayBuffer if it's not already
  if (data instanceof ArrayBuffer) {
    buf = new Uint8Array(data);
  } else if (data instanceof Blob) {
    buf = new Uint8Array(await data.arrayBuffer())
  } else if (typeof data === 'string') {
    // If it's a string, convert to ArrayBuffer
    const encoder = new TextEncoder();
    buf = new Uint8Array(encoder.encode(data).buffer);
  } else {
    throw new Error(`Unexpected event data type: ${data}`);
  }
  return WsResponse.decode(buf)
}